import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Search, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const TYPE_TONE = {
  project: "bg-[#0052FF] text-white",
  relatie: "bg-emerald-600 text-white",
  medewerker: "bg-amber-600 text-white",
  verkoopfactuur: "bg-purple-600 text-white",
  inkoopfactuur: "bg-orange-600 text-white",
};

export default function GlobalSearch() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q } });
        setResults(data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (r) => {
    setOpen(false);
    setQ("");
    navigate(r.path);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2 border border-zinc-200 rounded-sm px-3 py-2 bg-white focus-within:border-[#0052FF] focus-within:ring-1 focus-within:ring-[#0052FF]">
        {loading ? <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" /> : <Search className="w-4 h-4 text-zinc-400" />}
        <input
          type="text"
          placeholder={t("search.placeholder")}
          className="flex-1 bg-transparent outline-none text-sm"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          data-testid="global-search-input"
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg z-50 max-h-96 overflow-auto" data-testid="global-search-results">
          {results.length === 0 && !loading && (
            <div className="p-3 text-sm text-zinc-500">{t("search.noResults")}</div>
          )}
          {results.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              onClick={() => pick(r)}
              className="w-full text-left p-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 flex items-center gap-2"
              data-testid={`search-result-${r.type}-${r.id}`}
            >
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${TYPE_TONE[r.type] || "bg-zinc-500 text-white"}`}>{t(`search.types.${r.type}`, { defaultValue: r.type })}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{r.label}</div>
                {r.sub && <div className="text-xs text-zinc-500 truncate">{r.sub}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
